import { navigate } from "@reach/router";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useTheme } from "../../context/ThemeContext";
import { useBannerDetails } from "../../hooks/useBannerManager";
import { useProgramFees } from "../../hooks/useProgramFees";
import { useProgramTitles } from "../../hooks/useProgramTitles";
import ArrowRightBigIcon from "../../images/icons/arrow-right-big-icon.inline.svg";
import ArrowRight from "../../images/icons/arrow-right.inline.svg";
import TickOutline from "../../images/icons/tick-outline.inline.svg";
import {
  formatCurrencyAmount,
  shortcodePreProcessor
} from "../../utils/formatContent";
import ProgramCapacity from "../shared/programCapacity";
import ProgramRating from "../shared/programRating";

const _monthlist = Object.freeze([
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
]);

const DatesAndPrices = ({
  programId,
  exclusiveDiscounts,
  programCode,
  researchProgramCode,
  careerProgramCode,
  programTypes,
  programPopular,
  programCapacity,
  exclusiveOffers,
  scholarshipDisplayDescription,
  specialOffer
}) => {
  const { currentSiteMeta, localeLanguage } = useTheme();
  const currency =
    currentSiteMeta.currency ?? process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";
  const currencyToUse = currency
    ? currency.toLowerCase()
    : process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

  const [currencySymbol, setCurrencySymbol] = useState("");
  const [exclusiveDiscountAmount, setExclusiveDiscountAmount] = useState(0);

  const [getSelectedStartDate, setSelectedStartDate] = useState(false);

  const [getStartDates, setStartDates] = useState([]);
  const [getRawStartDates, setRawStartDates] = useState([]);
  const [getDurations, setDurationList] = useState([]);

  const [internshipType, setInternshipType] = useState(false);
  const [uniqYears, setUniqYears] = useState([]);
  const [monthIntList, setMonthIntList] = useState([]);
  const [useProgramCode, setProgramCodeToUse] = useState(programCode);

  useEffect(() => {
    setCurrencySymbol(
      currentSiteMeta?.currency_symbol ??
        process.env.GATSBY_PROJECT_CURRENCY ??
        ""
    );

    const exclusiveDiscount =
      exclusiveDiscounts.find(
        (discount) => discount.exclusiveDiscountCurrency === currencyToUse
      ) ?? [];

    setExclusiveDiscountAmount(
      exclusiveDiscount?.exclusiveDiscountAmount ?? "XXXX"
    );
  }, [currentSiteMeta, currencyToUse, exclusiveDiscounts]);

  let dateOptions = {
    month: "long",
    day: "numeric"
  };

  const updateStartDates = (year) => {
    setSelectedStartDate(false);
    setDurationList([]);

    setStartDates(_.uniqBy(_.sortBy(monthIntList[year], "month"), "date"));
    setRawStartDates(_.sortBy(monthIntList[year], "month"));
  };

  const updateDurations = async (date) => {
    setSelectedStartDate(false);
    setDurationList([]);

    const durations = getRawStartDates
      .filter((stdate) => stdate.date === date)
      .flatMap((res) => res.price);

    setSelectedStartDate(true);
    setDurationList(_.sortBy(_.uniqBy(durations, "Duration"), "Duration"));
  };

  const fetchPrices = useCallback(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const getdates = async () => {
      const res = await fetch(
        `/api/salesforce/programdates?program_code=${useProgramCode}`,
        { signal: signal }
      );
      const durations = await res.json();

      if (!durations) {
        // console.error("no durations", durations);
        return [];
      }

      const dateGroup = durations.reduce(
        (
          prevVal,
          currentVal,
          currentIndex,
          theArray,
          keyVal = currentVal.InstanceDate
        ) => (
          (prevVal[keyVal] || (prevVal[keyVal] = [])).push(currentVal), prevVal
        ),
        {}
      );

      //get dates and durations
      let t_monthIntList = [];
      Object.keys(dateGroup).forEach((date) => {
        let year = date.substring(0, 4);
        let month = date.substring(5, 7);
        let day = date.substring(8, 10);

        let durationList = [];
        dateGroup[date]?.forEach((i) => {
          durationList[i.Duration] = i.DurationDesc;
        });

        t_monthIntList[parseInt(year)] = t_monthIntList[parseInt(year)] ?? [];
        t_monthIntList[parseInt(year)]?.push({
          month: parseInt(month),
          month_full: _monthlist[parseInt(month) - 1],
          date: date,
          date_parse: new Date([month, day, year].join("-")),
          date_formatted: new Intl.DateTimeFormat("en-GB", dateOptions).format(
            new Date([month, day, year].join("/"))
          ),
          duration_desc: dateGroup[date]?.map((i) => i.DurationDesc),
          currency: currencyToUse,
          price: dateGroup[date].map(
            (t) =>
              t.ProductPrices.filter(
                (it) => it.CurrencyIso.toLowerCase() === currencyToUse
              )
                .map((filD) => {
                  return {
                    ...filD,
                    DurationDesc: durationList[filD.Duration]
                  };
                })
                .shift() ?? ""
          ),
          debug: dateGroup[date]
        });
      });

      // console.debug("t_monthIntList", t_monthIntList);

      setUniqYears(Object.keys(t_monthIntList));
      setMonthIntList(t_monthIntList);
    };

    getdates().catch(console.error);

    return () => {
      controller.abort();
    };
  }, [useProgramCode, currencyToUse, currentSiteMeta]);

  useEffect(fetchPrices, [currentSiteMeta, useProgramCode, currencyToUse]);

  // Form submit
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  const [status, setStatus] = useState("");

  const onSubmit = async (data) => {
    let research_intership = data?.research_intership;

    const formFields = {
      form_type: "dates_prices_form",
      research_intership: research_intership,
      program_id: programId,
      departure_date: data?.start_date,
      duration: data?.duration ?? "",
      programCode: useProgramCode
    };

    //short month departure date format

    const formatted_departure_date =
      data?.start_date && data?.start_date !== "start_date"
        ? data?.start_date
        : "";
    // console.debug("start_date", data.start_date);

    setStatus("processing");

    const apiUrl = `/application-form/?departure_date=${formatted_departure_date}&duration=${formFields.duration}&program_code=${formFields.programCode}`;
    setStatus("success");
    reset();
    await navigate(apiUrl);
  };

  const updateInternshipType = (e) => {
    setSelectedStartDate(false);
    setDurationList([]);
    setStartDates([]);
    setUniqYears([]);

    let internProgramCode = programCode;
    if (e.target.value.toLowerCase().includes("career")) {
      internProgramCode = careerProgramCode ?? programCode;
      setInternshipType(true);
    } else if (e.target.value.toLowerCase().includes("research")) {
      internProgramCode = researchProgramCode ?? programCode;
      setInternshipType(true);
    }

    setProgramCodeToUse(internProgramCode);
    fetchPrices();
  };

  const programType =
    programTypes && programTypes.length
      ? programTypes.map((pgmType) => pgmType.title).join("<br/>")
      : "";

  const groupProgramType = programTypes?.nodes
    ?.map((pgmType) => pgmType.name)
    .join("<br/>");

  const groupProgramTypes =
    groupProgramType?.toLowerCase().includes("schools") ||
    groupProgramType?.toLowerCase().includes("university");

  const programFees = useProgramFees() || [];
  const programSubtitles = useProgramTitles() || "";

  const datesCostBriefSummary = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description: programSubtitles?.datesCostsBriefSummary || ""
  });

  const datesCostSubtitle = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description: programSubtitles?.datesCostsSubtitle || ""
  });

  const grantsDetails = useBannerDetails(null, "dates");

  const datesGrantsTitle = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description: grantsDetails?.title || ""
  });

  const datesGrantsDesc = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description: grantsDetails?.desc || ""
  });

  const showPricingExtra = false;

  return (
    <>
      <h2 className="heading text-left py-5 lato-300-latin">
        Dates and prices
      </h2>

      {showPricingExtra && (
        <div
          className="sub-title text-base mb-5 font-body font-bold planet:text-white text-gray-lighter"
          dangerouslySetInnerHTML={{ __html: datesCostSubtitle }}
        />
      )}

      <div className="divide-y width-divider" />
      {!status && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col justify-between w-full"
        >
          <ProgramRating
            outerContainerClass={"items-end pt-6 justify-end"}
            ratingIconClass={"fill-generic-text h-4"}
            ratingLogoClass={"fill-gray-lighter h-4"}
          />

          <div className="py-4">
            <h3 className="text-left lato-300-latin text-xl planet:gvi-light-gray-text">
              Select a start date:
            </h3>
          </div>

          <div className="flex flex-col sm:flex-row">
            <label className="w-full sm:pr-2 sm:w-1/2">
              <select
                id="year"
                name="year"
                autoComplete="year"
                {...register("year", { required: true })}
                className="mt-1 block w-full py-2 px-3 bg-transparent gvi-border-05 p-2 rounded-sm shadow-sm focus:outline-none border-gray-generic planet:text-white text-gray-generic"
                onChange={(event) => updateStartDates(event.target.value)}
              >
                <option className="planet:bg-gray-dark">Select year</option>
                {uniqYears.sort().map((year, idx) => (
                  <option
                    value={year}
                    key={`year-option-${idx}`}
                    className="planet:bg-gray-dark"
                  >
                    {year}
                  </option>
                ))}
              </select>
              {errors.year && (
                <span className="text-red-500">Please select year</span>
              )}
            </label>
            {!groupProgramTypes && (
              <label className="w-full sm:w-1/2 sm:pl-2">
                <select
                  id="start_date"
                  name="start_date"
                  autoComplete="month"
                  {...register("start_date", { required: true })}
                  className="mt-1 block w-full py-2 px-3 bg-transparent gvi-border-05 p-2 rounded-sm shadow-sm focus:outline-none border-gray-generic planet:text-white text-gray-generic"
                  onChange={(event) => updateDurations(event.target.value)}
                >
                  <option className="planet:bg-gray-dark" value="start_date">
                    Select a start date
                  </option>
                  {getStartDates?.map((month, idx) => (
                    <option
                      className="planet:bg-gray-dark"
                      value={month.date}
                      key={`month-option-${idx}`}
                    >
                      {month.date_formatted}
                    </option>
                  ))}
                </select>
                {errors.start_date && (
                  <span className="text-red-500">Please select start date</span>
                )}
              </label>
            )}
          </div>

          {programPopular && programCapacity && (
            <ProgramCapacity
              programCapacityText={programCapacity}
              outerContainerClass={"pt-8"}
            />
          )}

          {getDurations && getDurations.length > 0 && (
            <>
              <div className="py-8">
                <h3 className="text-left lato-300-latin text-xl planet:gvi-light-gray-text">
                  Select a duration:
                </h3>
              </div>
              <div className="flex flex-row">
                <div className="w-full">
                  {getDurations?.map((startDte, idx) => (
                    <div className="" key={`duration-input-${idx}`}>
                      <label
                        htmlFor={startDte.Duration}
                        className="text-generic-text font-medium planet:text-white ml-2 flex items-center hover:border rounded-sm hover:gvi-border-gray p-4"
                      >
                        <input
                          id={startDte.Duration}
                          type="radio"
                          name="duration"
                          value={startDte.DurationDesc}
                          className="mr-4 h-3 w-3 appearance-none text-white planet:text-gray-dark ring-1 ring-offset-4 ring-offset-current ring-gray-light planet:ring-white rounded-full checked:ring-2 checked:ring-yellow-lighten checked:ring-text-yellow-lighten checked:text-yellow-lighten planet:checked:ring-green-standard planet:checked:text-green-standard"
                          {...register("duration", { required: true })}
                        />
                        {startDte.DurationDesc}
                        <div
                          className={`${
                            showPricingExtra ? "hidden" : "pl-2"
                          } planet:block`}
                        >
                          {"  -  "}
                          <span
                            className="text-border-light planet:gvi-light-gray-text ml-1"
                            dangerouslySetInnerHTML={{
                              __html: formatCurrencyAmount({
                                currentSiteMeta,
                                currency: startDte.CurrencyIso.toLowerCase(),
                                amount: startDte.UnitPrice
                              })
                            }}
                          />
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {getSelectedStartDate && getDurations && !getDurations.length && (
            <>
              <div className="py-8">
                <h3 className="text-left lato-300-latin text-xl planet:gvi-light-gray-text">
                  Continue your application below for prices and duration
                  options.
                </h3>
              </div>
            </>
          )}

          {showPricingExtra && datesCostBriefSummary && (
            <div className="relative">
              <div className="grid grid-flow-col gap-5 mt-12 items-center">
                <div className="w-0.75 h-5/6 bg-yellow-lighten planet:bg-white block" />
                <div
                  className="text-generic-text lato-regular-latin planet:gvi-light-gray-text"
                  dangerouslySetInnerHTML={{ __html: datesCostBriefSummary }}
                />
              </div>
            </div>
          )}

          {grantsDetails?.title ? (
            <div className="border-tertiary-bg planet:border-white rounded-md sm:rounded-xs border-1 flex items-center flex-col sm:flex-row sm:h-full mt-12 bg-secondary-bg planet:gvi-light-gray-bg overflow-hidden">
              <div className="flex flex-col items-start flex-1 w-full gap-2 p-6 rounded-b bg-secondary-bg planet:gvi-light-gray-bg">
                <div className="w-full flex flex-col md:flex-row">
                  <h3
                    className="heading"
                    dangerouslySetInnerHTML={{
                      __html: datesGrantsTitle
                    }}
                  />
                </div>
                <div
                  className="description w-full text-sm text-black-lighter planet:text-gray-generic"
                  dangerouslySetInnerHTML={{
                    __html: datesGrantsDesc
                  }}
                />
              </div>
            </div>
          ) : (
            specialOffer.offerHeading && (
              <div className="border-tertiary-bg planet:border-white rounded-xs sm:rounded-xs border-1 flex items-center flex-col sm:flex-row sm:h-full mt-12 bg-secondary-bg planet:gvi-light-gray-bg overflow-hidden">
                <div className="flex flex-col items-start flex-1 w-full gap-2 p-6 rounded-b bg-secondary-bg planet:gvi-light-gray-bg">
                  <div className="w-full flex flex-col md:flex-row">
                    <h3
                      className="heading !pb-0 pr-2"
                      dangerouslySetInnerHTML={{
                        __html: specialOffer.offerHeading
                      }}
                    />
                    <h3
                      className="subheading"
                      dangerouslySetInnerHTML={{
                        __html: specialOffer.offerSubheading
                      }}
                    />
                  </div>
                  <div
                    className="description w-full md:w-5/6 text-base text-black-lighter planet:text-gray-generic"
                    dangerouslySetInnerHTML={{
                      __html: specialOffer.offerDesc
                    }}
                  />
                </div>
              </div>
            )
          )}

          <div className="flex flex-col pt-6">
            {exclusiveOffers.length > 0 &&
              exclusiveOffers?.map((extraItem, idx) => (
                <div className="flex flex-row py-6" key={`extra-item-${idx}`}>
                  <div className="w-full flex items-top ">
                    <div className="mr-3">
                      <TickOutline className="h-6 w-6 stroke-yellow-lighten planet:stroke-white" />
                    </div>

                    <div
                      className="text-generic-text lato-regular-latin planet:gvi-light-gray-text"
                      dangerouslySetInnerHTML={{
                        __html: `<span class="font-bold planet:text-white">${
                          extraItem.title
                        }</span> ${
                          idx === 0
                            ? scholarshipDisplayDescription
                            : extraItem.description
                        }`
                      }}
                    />
                  </div>
                </div>
              ))}

            <div className="py-4">
              <h3 className="text-left lato-300-latin text-xl pb-4 planet:gvi-light-gray-text">
                What happens next?
              </h3>
              <p className="text-generic-text lato-regular-latin planet:text-white">
                Once you apply, a personal Enrollment Manager will be assigned
                to walk you through the rest of the process.
              </p>
            </div>

            <div className="flex w-full sm:w-1/2">
              <button
                type="submit"
                className="flex items-center gap-2 mt-2 justify-center focus:outline-none text-black-lighter planet:text-gray-darker text-sm sm:text-base font-bold planet:gvi-bg-green planet:rounded-sm py-2 w-full border rounded-full border-black-lighter planet:border-0 "
                onClick={handleSubmit(onSubmit)}
              >
                {groupProgramTypes
                  ? "Enquire as an educator"
                  : "Continue my application"}
                <span className="ml-1">
                  <ArrowRight
                    className={`w-3 h-3 stroke-gray-darker hidden planet:block`}
                  />
                  <ArrowRightBigIcon className="fill-black-lighter block planet:hidden" />
                </span>
              </button>
            </div>
          </div>
        </form>
      )}
      {status === "processing" && (
        <div className=" text-center h2 font-normal w-full flex items-center justify-center h-56">
          ...processing
        </div>
      )}
      {status === "success" && (
        <div className=" text-center h2 font-normal w-full flex items-center justify-center h-56">
          Redirecting you to the application form
        </div>
      )}
    </>
  );
};

DatesAndPrices.propTypes = {
  programId: PropTypes.number.isRequired,
  programCode: PropTypes.string.isRequired,
  researchProgramCode: PropTypes.string,
  careerProgramCode: PropTypes.string,
  exclusiveDiscounts: PropTypes.array,
  programTypes: PropTypes.array
};

export default DatesAndPrices;
